import { produce, castDraft } from 'immer';
import { cloneDeep } from 'lodash';
import type { WritableDeep } from 'type-fest';

import { ensureDefined, invariant, isDefined } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';

import { DocumentEditorTwoStepContentActionType } from '../../enums/two-step-content-action-type.enum';
import { DocumentEditorInstantaneousContentActionType } from '../../enums/instantaneous-content-action-type.enum';
import { getDeepSection } from '../../utils/get-deep-section.util';
import { getVirtualRoot } from '../../utils/get-virtual-root.util';
import { comparePaths } from '../../utils/compare-paths.util';
import { materializeInsertionPoints } from '../../utils/materialize-insertion-points.util';
import { DocumentEditorActionType } from '../../enums/action-type.enum';
import type { DocumentEditorState } from '../../types/editor-state.type';
import type { Editable } from '../../types/editable.type';
import type { DocumentEditorAction } from '../../types/actions.type';
import type { DocumentEditorContentAction } from '../../types/content-actions.type';
import type { DocumentEditorCmp_RecursiveSection } from '../../fragments/recursive-section.fragment';

import { insertSections, InsertPosition } from '../utils/insert-section.util';
import type { ReducerContext } from '../types/reducer-context.type';

export const contentMutationsReducer = <E extends Editable>(
  state: Readonly<DocumentEditorState<E>>,
  action:
    Readonly<DocumentEditorAction<E>> | Readonly<DocumentEditorContentAction>,
  context: Readonly<ReducerContext<E>>,
): Readonly<DocumentEditorState<E>> | null => {
  switch (action.type) {
    case DocumentEditorTwoStepContentActionType.InsertBefore:
    case DocumentEditorTwoStepContentActionType.InsertAfter:
    case DocumentEditorTwoStepContentActionType.InsertInside:
    case DocumentEditorTwoStepContentActionType.InsertStart:
    case DocumentEditorTwoStepContentActionType.InsertEnd: {
      invariant(!!action.payload.sections.length, 'Missing section');
      invariant(
        !!state.currentTwoStepContentAction,
        'Current two step content action not set',
      );

      const newPastVersions = context.pushInPast(state);

      return produce(state, (draft) => {
        if (action.payload.insertionPointRealPath) {
          const materialized = materializeInsertionPoints({
            state,
            insertionPointRealPaths: [action.payload.insertionPointRealPath],
            templateDependencies: context.templateDependencies,
            contentOriginsCodeMap: context.contentOriginsCodeMap,
            getEditable: context.getEditable,
          });
          draft.editable = castDraft(
            materialized as E,
          ) as typeof draft.editable;
        }

        const positionMap = {
          [DocumentEditorTwoStepContentActionType.InsertBefore]:
            InsertPosition.Before,
          [DocumentEditorTwoStepContentActionType.InsertAfter]:
            InsertPosition.After,
          [DocumentEditorTwoStepContentActionType.InsertInside]:
            InsertPosition.Inside,
          [DocumentEditorTwoStepContentActionType.InsertStart]:
            InsertPosition.Start,
          [DocumentEditorTwoStepContentActionType.InsertEnd]:
            InsertPosition.End,
        };

        const { currentTwoStepContentActionPath } = ensureDefined({
          currentTwoStepContentActionPath:
            state.currentTwoStepContentAction?.path,
        });

        insertSections({
          clonedEditable: draft.editable as WritableDeep<E>,
          path: currentTwoStepContentActionPath,
          sections: action.payload.sections,
          position: positionMap[action.type],
          context,
        });

        draft.currentTwoStepContentAction = undefined;
        draft.isDrawerOpen = false;
        draft.pastVersions = castDraft(newPastVersions);
        draft.futureVersions = [];
        draft.currentVersion = state.currentVersion + 1;
      });
    }

    case DocumentEditorTwoStepContentActionType.Update: {
      invariant(
        !!state.currentTwoStepContentAction,
        'Current two step content action not set',
      );
      invariant(!!action.payload.sections.length, 'Missing section');

      const { actionFirstSection } = ensureDefined(
        { actionFirstSection: action.payload.sections[0] },
        'Error in update action.',
      );

      const newPastVersions = context.pushInPast(state);

      return produce(state, (draft) => {
        if (action.payload.insertionPointRealPath) {
          const materialized = materializeInsertionPoints({
            state,
            insertionPointRealPaths: [action.payload.insertionPointRealPath],
            templateDependencies: context.templateDependencies,
            contentOriginsCodeMap: context.contentOriginsCodeMap,
            getEditable: context.getEditable,
          });
          draft.editable = castDraft(
            materialized as E,
          ) as typeof draft.editable;
        }

        const { currentTwoStepContentActionPath } = ensureDefined({
          currentTwoStepContentActionPath:
            state.currentTwoStepContentAction?.path,
        });

        const section = getDeepSection(
          context.getEditable<true>(draft.editable as WritableDeep<E>)
            .virtualRoot,
          currentTwoStepContentActionPath,
        );

        section.content = actionFirstSection.content;
        section.title = actionFirstSection.title;

        draft.currentTwoStepContentAction = undefined;
        draft.isDrawerOpen = false;
        draft.pastVersions = castDraft(newPastVersions);
        draft.futureVersions = [];
        draft.currentVersion = state.currentVersion + 1;
      });
    }

    case DocumentEditorInstantaneousContentActionType.Delete: {
      const { path } = action.payload;
      const parentPath = path.slice(0, -1);
      const { subSectionIndex } = ensureDefined(
        { subSectionIndex: path.slice(-1)[0] },
        'Error in delete action.',
      );

      const newPastVersions = context.pushInPast(state);

      return produce(state, (draft) => {
        if (action.payload.insertionPointRealPath) {
          const materialized = materializeInsertionPoints({
            state,
            insertionPointRealPaths: [action.payload.insertionPointRealPath],
            templateDependencies: context.templateDependencies,
            contentOriginsCodeMap: context.contentOriginsCodeMap,
            getEditable: context.getEditable,
          });
          draft.editable = castDraft(
            materialized as E,
          ) as typeof draft.editable;
        }

        const parent = getDeepSection(
          context.getEditable<true>(draft.editable as WritableDeep<E>)
            .virtualRoot,
          parentPath,
        );

        parent.subSections.splice(subSectionIndex, 1);

        draft.pastVersions = castDraft(newPastVersions);
        draft.futureVersions = [];
        draft.currentVersion = state.currentVersion + 1;
      });
    }

    case DocumentEditorInstantaneousContentActionType.Reinitialize: {
      const { path } = action.payload;
      const parentPath = path.slice(0, -1);
      const { subSectionIndex } = ensureDefined(
        { subSectionIndex: path.slice(-1)[0] },
        'Error in reinitialize action.',
      );

      const newPastVersions = context.pushInPast(state);

      return produce(state, (draft) => {
        if (action.payload.insertionPointRealPath) {
          const materialized = materializeInsertionPoints({
            state,
            insertionPointRealPaths: [action.payload.insertionPointRealPath],
            templateDependencies: context.templateDependencies,
            contentOriginsCodeMap: context.contentOriginsCodeMap,
            getEditable: context.getEditable,
          });
          // eslint-disable-next-line functional/immutable-data
          draft.editable = castDraft(
            materialized as E,
          ) as typeof draft.editable;
        }

        const section = getDeepSection(
          context.getEditable<true>(draft.editable as WritableDeep<E>)
            .virtualRoot,
          path,
        );

        const parent = getDeepSection(
          context.getEditable<true>(draft.editable as WritableDeep<E>)
            .virtualRoot,
          parentPath,
        );

        const { template } = ensureDefined(
          {
            template: context.templateDependencies.find(
              (templateDependency) =>
                templateDependency._id === section.template?._id,
            ),
          },
          'Template not found',
        );

        const templateVirtualRoot = getVirtualRoot(template);

        const reinitializedSection: WritableDeep<DocumentEditorCmp_RecursiveSection> =
          {
            __typename: 'Section' as const,
            _id: section._id,
            template: section.template,
            contentOriginId: context.contentOriginsCodeMap.Catalog._id,
            isVirtual: true,
            isInsertionPoint: true,
            orientation: templateVirtualRoot.orientation,
            paperFormat: templateVirtualRoot.paperFormat,
            hasPagebreakAfter: templateVirtualRoot.hasPagebreakAfter,
            hasPagebreakBefore: templateVirtualRoot.hasPagebreakBefore,
            subSections: [],
            customLayouts: [],
          };

        parent.subSections.splice(subSectionIndex, 1, reinitializedSection);

        draft.pastVersions = castDraft(newPastVersions);
        draft.futureVersions = [];
        draft.currentVersion = state.currentVersion + 1;
      });
    }

    case DocumentEditorInstantaneousContentActionType.Duplicate: {
      const { path } = action.payload;
      const parentPath = path.slice(0, -1);
      const [subSectionIndex] = path.slice(-1);

      invariant(
        isDefined(subSectionIndex),
        'Error in duplicate action, subSectionIndex undefined.',
      );

      const newPastVersions = context.pushInPast(state);

      return produce(state, (draft) => {
        if (action.payload.insertionPointRealPath) {
          const materialized = materializeInsertionPoints({
            state,
            insertionPointRealPaths: [action.payload.insertionPointRealPath],
            templateDependencies: context.templateDependencies,
            contentOriginsCodeMap: context.contentOriginsCodeMap,
            getEditable: context.getEditable,
          });
          // eslint-disable-next-line functional/immutable-data
          draft.editable = castDraft(
            materialized as E,
          ) as typeof draft.editable;
        }

        const parent = getDeepSection(
          context.getEditable<true>(draft.editable as WritableDeep<E>)
            .virtualRoot,
          parentPath,
        );

        const { section } = ensureDefined(
          { section: parent.subSections[subSectionIndex] },
          'Error in duplicate action, section undefined.',
        );

        // Clone is still needed here for duplicating the section
        const clonedElement = cloneDeep(section);

        // Recursively update ids
        const updateIds = (
          sec: WritableDeep<DocumentEditorCmp_RecursiveSection>,
        ) => {
          sec._id = context.generateId();
          sec.subSections.forEach((subSection) => {
            updateIds(
              subSection as WritableDeep<DocumentEditorCmp_RecursiveSection>,
            );
          });
        };

        updateIds(
          clonedElement as WritableDeep<DocumentEditorCmp_RecursiveSection>,
        );

        parent.subSections.splice(subSectionIndex + 1, 0, clonedElement);

        draft.pastVersions = castDraft(newPastVersions);
        draft.futureVersions = [];
        draft.currentVersion = state.currentVersion + 1;
      });
    }

    case DocumentEditorInstantaneousContentActionType.Move: {
      const insertionPoints = [
        ...(action.payload.oldInsertionPointRealPath
          ? [action.payload.oldInsertionPointRealPath]
          : EMPTY_ARRAY),
        ...(action.payload.newInsertionPointRealPath
          ? [action.payload.newInsertionPointRealPath]
          : EMPTY_ARRAY),
      ];

      const [newStart] = action.payload.newPath.slice(-1);
      const [oldStart] = action.payload.oldPath.slice(-1);

      invariant(
        isDefined(newStart) && isDefined(oldStart),
        'Error in move action, start not found.',
      );

      const newPastVersions = context.pushInPast(state);

      // A move between sections of the document itself involves no
      // insertion point of a template: nothing to materialize.
      const [firstInsertionPoint] = insertionPoints;

      return produce(state, (draft) => {
        if (firstInsertionPoint) {
          const materialized = materializeInsertionPoints({
            state,
            insertionPointRealPaths: [firstInsertionPoint],
            templateDependencies: context.templateDependencies,
            contentOriginsCodeMap: context.contentOriginsCodeMap,
            getEditable: context.getEditable,
          });
          draft.editable = castDraft(
            materialized as E,
          ) as typeof draft.editable;
        }

        const section = getDeepSection(
          context.getEditable<true>(draft.editable as WritableDeep<E>)
            .virtualRoot,
          action.payload.oldPath,
        );

        const oldParent = getDeepSection(
          context.getEditable<true>(draft.editable as WritableDeep<E>)
            .virtualRoot,
          action.payload.oldPath.slice(0, -1),
        );

        const newParent = getDeepSection(
          context.getEditable<true>(draft.editable as WritableDeep<E>)
            .virtualRoot,
          action.payload.newPath.slice(0, -1),
        );

        if (comparePaths(action.payload.oldPath, action.payload.newPath) < 1) {
          newParent.subSections.splice(
            newStart,
            0,
            section as WritableDeep<DocumentEditorCmp_RecursiveSection>,
          );
          oldParent.subSections.splice(oldStart, 1);
        } else {
          oldParent.subSections.splice(oldStart, 1);
          newParent.subSections.splice(
            newStart,
            0,
            section as WritableDeep<DocumentEditorCmp_RecursiveSection>,
          );
        }

        draft.pastVersions = castDraft(newPastVersions);
        draft.futureVersions = [];
        draft.currentVersion = state.currentVersion + 1;
      });
    }

    case DocumentEditorActionType.OpenDrawer:
    case DocumentEditorActionType.CloseDrawer:
    case DocumentEditorActionType.FoldAllSections:
    case DocumentEditorActionType.UnfoldAllSections:
    case DocumentEditorActionType.SetEditorMode:
    case DocumentEditorActionType.RequestTwoStepContentAction:
    case DocumentEditorActionType.ClearTwoStepContentAction:
    case DocumentEditorActionType.Undo:
    case DocumentEditorActionType.Redo:
    case DocumentEditorActionType.ClearHistory:
    case DocumentEditorActionType.ClearNavigationTarget:
    case DocumentEditorActionType.BumpLatestSavedVersion:
    case DocumentEditorActionType.UpdateInfo:
    case DocumentEditorActionType.UpdateAdmin:
    case DocumentEditorInstantaneousContentActionType.SetFoldingMode:
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakAfter:
    case DocumentEditorInstantaneousContentActionType.SetPageBreakAfter:
    case DocumentEditorInstantaneousContentActionType.SetOrientation:
    case DocumentEditorInstantaneousContentActionType.SetPaperFormat:
    case DocumentEditorInstantaneousContentActionType.SetDisplayCondition:
    default: {
      return null; // Not handled by this reducer
    }
  }
};
